import React, { useEffect, useState, useRef } from "react";
import { AttendanceService, AttendanceRecord } from "@/services/attendanceService";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { FaLock, FaCheckCircle, FaArrowLeft, FaClock, FaUsers, FaChild, FaStar } from "react-icons/fa";
import { useAuth } from "@/hooks/useAuth";
import { motion, AnimatePresence } from "framer-motion";
import { formatTimeRange } from "@/lib/timeFormat";

// TODO: Replace with real feature gating logic
const isCheckinEnabled = true;

// Celebration confetti component
const Confetti = () => {
  const colors = ['#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7'];
  
  return (
    <div className="fixed inset-0 pointer-events-none z-50">
      {Array.from({ length: 50 }).map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-3 h-3 rounded-full"
          style={{
            backgroundColor: colors[Math.floor(Math.random() * colors.length)],
            left: `${Math.random() * 100}%`,
            top: `-10px`,
          }}
          initial={{ y: -10, x: 0, rotate: 0 }}
          animate={{
            y: window.innerHeight + 10,
            x: Math.random() * 200 - 100,
            rotate: 360,
          }}
          transition={{
            duration: 3 + Math.random() * 2,
            ease: "easeOut",
            delay: Math.random() * 0.5,
          }}
        />
      ))}
    </div>
  );
};

// Success celebration component
const SuccessAnimation = ({ student, time, onComplete }: { student: any; time: string; onComplete: () => void }) => {
  const [showConfetti, setShowConfetti] = useState(true);
  
  useEffect(() => {
    const confettiTimer = setTimeout(() => setShowConfetti(false), 3000);
    const completeTimer = setTimeout(onComplete, 4000);
    
    return () => {
      clearTimeout(confettiTimer);
      clearTimeout(completeTimer);
    };
  }, [onComplete]);

  return (
    <motion.div
      className="fixed inset-0 z-40 flex items-center justify-center"
      style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      }}
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.8 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
    >
      {showConfetti && <Confetti />}
      
      <div className="text-center text-white px-8">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, duration: 0.6, type: "spring", stiffness: 200 }}
        >
          <FaCheckCircle size={120} className="mx-auto mb-6 text-green-300" />
        </motion.div>
        
        <motion.h1
          className="text-6xl font-bold mb-4"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.6 }}
        >
          Welcome, {student.first_name}! 🎉
        </motion.h1>
        
        <motion.div
          className="text-2xl mb-4"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.6 }}
        >
          You're all checked in for your class!
        </motion.div>
        
        <motion.div
          className="text-xl text-green-200"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.6 }}
        >
          Check-in time: {time}
        </motion.div>
        
        <motion.div
          className="mt-8 text-lg"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2, duration: 1 }}
        >
          Have an amazing class! ⭐
        </motion.div>
      </div>
    </motion.div>
  );
};

// Time-based greeting
const getTimeBasedGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return { greeting: "Good Morning!", emoji: "🌅", gradient: "from-orange-300 to-yellow-400" };
  if (hour < 17) return { greeting: "Good Afternoon!", emoji: "☀️", gradient: "from-blue-300 to-blue-500" };
  return { greeting: "Good Evening!", emoji: "🌙", gradient: "from-purple-400 to-indigo-600" };
};

// Enhanced student card component
const StudentCard = ({ student, checked, isLoading, onCheckIn, streak }: any) => {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <motion.div
      className={`
        relative overflow-hidden rounded-3xl border-4 transition-all duration-300 cursor-pointer
        ${checked 
          ? 'bg-gradient-to-br from-green-100 to-green-200 border-green-400 shadow-lg shadow-green-200' 
          : 'bg-white border-gray-200 shadow-lg hover:shadow-xl hover:scale-105'
        }
      `}
      style={{ minWidth: '280px', minHeight: '220px' }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      whileHover={{ scale: checked ? 1 : 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      {/* Background pattern for unchecked cards */}
      {!checked && (
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-purple-500" />
        </div>
      )}
      
      {/* Streak badge */}
      {streak > 1 && (
        <motion.div
          className="absolute top-2 right-2 bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-3 py-1 rounded-full text-sm font-bold shadow-lg"
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
        >
          🔥 {streak} weeks!
        </motion.div>
      )}
      
      <div className="relative p-6 flex flex-col items-center justify-center h-full">
        {/* Profile Picture */}
        <div className={`
          w-20 h-20 rounded-full border-4 mb-4 overflow-hidden
          ${checked ? 'border-green-400' : 'border-gray-300'}
        `}>
          {student.profile_picture_url ? (
            <img 
              src={student.profile_picture_url} 
              alt={student.first_name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className={`
              w-full h-full flex items-center justify-center text-3xl
              ${checked ? 'bg-green-200 text-green-600' : 'bg-gray-200 text-gray-500'}
            `}>
              <FaChild />
            </div>
          )}
        </div>
        
        {/* Student Name */}
        <h3 className={`
          text-2xl font-bold text-center mb-3
          ${checked ? 'text-green-800' : 'text-gray-800'}
        `}>
          {student.first_name} {student.last_name}
        </h3>
        
        {/* Status/Action */}
        {checked ? (
          <div className="text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 200, damping: 10 }}
            >
              <FaCheckCircle size={32} className="text-green-500 mx-auto mb-2" />
            </motion.div>
            <div className="text-green-700 font-semibold text-lg">Checked In!</div>
            <div className="text-green-600 text-sm mt-1">
              {new Date(checked.created_at).toLocaleTimeString([], { 
                hour: "2-digit", 
                minute: "2-digit",
                hour12: true
              })}
            </div>
          </div>
        ) : (
          <motion.button
            className={`
              px-8 py-4 rounded-2xl font-bold text-xl transition-all duration-200
              ${isLoading 
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                : 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg hover:shadow-xl'
              }
            `}
            disabled={isLoading}
            onClick={() => onCheckIn(student)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {isLoading ? (
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 border-2 border-gray-500 border-t-transparent rounded-full animate-spin" />
                Checking In...
              </div>
            ) : (
              <>
                <FaStar className="inline mr-2" />
                Check In
              </>
            )}
          </motion.button>
        )}
      </div>
    </motion.div>
  );
};

const CheckIn: React.FC = () => {
  const [classes, setClasses] = useState<any[]>([]);
  const [selectedClass, setSelectedClass] = useState<any | null>(null);
  const [students, setStudents] = useState<any[]>([]);
  const [checkedIn, setCheckedIn] = useState<Record<string, AttendanceRecord>>({});
  const [loading, setLoading] = useState(false);
  const [checkingIn, setCheckingIn] = useState<string | null>(null);
  const [showCelebration, setShowCelebration] = useState<{ student: any; time: string } | null>(null);
  const [lastWeekClass, setLastWeekClass] = useState<string | null>(null);
  const [staffInfo, setStaffInfo] = useState<any>(null);
  const [studentStreaks, setStudentStreaks] = useState<Record<string, number>>({});
  const { organizationId } = useAuth();
  
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Sound effects
  useEffect(() => {
    audioRef.current = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBShJo+TwtWMhCTSJz+XNjj0PJ3rJ8d2QQAoUXrTp66hVFAtHn+DyvmwhBSpJo+TwtWMbCjOGz+XOiTwOJ3rJ8d2QQAoUXrTp66hVEwtGn+DyvmwhBipJo+TwtWMcCjOGz+XOiTwOJ3rJ8d2QQAoUXrTp66hVEwtGn+DyvmwhBipJo+TwtWMcCjOGz+XOiTwOJ3rJ8d2QQAoUXrTp66hVEwtGn+DyvmwhBipJo+TwtWMcCjOGz+XOiTwPNYHX9tiQSAoOXrb+7atUCgw+i+PEfCUEFIjU9N+IOAozgLzmygkEFoHW8tiQQAoOXrba4KhVDgtGn+DyvmwhBitJo+TwtWMcCjOGz+XOiTwPNYHX9tiQSAoOXrb+7atUCgxOl+XQfzUEHYzW9N+IOAozgLzmygkNbAcDUqPLozZIA9YNutNp66hKC0g9n+vywmwjBzqO2OOgVgQGWKbs4r4I=');
  }, []);

  const playSuccessSound = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {
        // Ignore audio play errors (some browsers require user interaction)
      });
    }
  };

  useEffect(() => {
    document.body.classList.add('kiosk-fullscreen');
    return () => {
      document.body.classList.remove('kiosk-fullscreen');
    };
  }, []);

  // Fetch today's classes on mount and check for last week's attendance
  useEffect(() => {
    const fetchClasses = async () => {
      if (!organizationId) {
        toast.error("No organization ID found. Please log in again.");
        setClasses([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      const today = new Date().toISOString().slice(0, 10);
      const { data, error } = await supabase
        .from("classes")
        .select("id, name, level, location, organization_id, class_schedules!class_schedules_class_id_fkey(start_time, end_time)")
        .eq("is_active", true)
        .eq("organization_id", organizationId);
      if (error) {
        toast.error("Failed to load classes");
      } else {
        setClasses(data || []);
        
        // Check for last week's attendance to enable express check-in
        const lastWeek = new Date();
        lastWeek.setDate(lastWeek.getDate() - 7);
        const lastWeekDate = lastWeek.toISOString().slice(0, 10);
        
        const { data: lastWeekAttendance } = await supabase
          .from("class_attendance")
          .select("class_id")
          .eq("organization_id", organizationId)
          .eq("date", lastWeekDate)
          .eq("status", "present")
          .limit(1);
          
        if (lastWeekAttendance && lastWeekAttendance.length > 0) {
          setLastWeekClass(lastWeekAttendance[0].class_id);
        }
      }
      setLoading(false);
    };
    fetchClasses();
  }, [organizationId]);

  // Fetch students for selected class and calculate streaks
  useEffect(() => {
    if (!selectedClass) return;
    const fetchStudents = async () => {
      setLoading(true);
      
      // Get enrolled students
      const { data, error } = await supabase
        .from("enrollments")
        .select("student:students(id, first_name, last_name, profile_picture_url)")
        .eq("class_id", selectedClass.id)
        .eq("status", "active");
      if (error) {
        toast.error("Failed to load students");
        setStudents([]);
      } else {
        const studentList = (data || []).map((e: any) => e.student);
        setStudents(studentList);
        
        // Calculate attendance streaks for each student
        const streaks: Record<string, number> = {};
        for (const student of studentList) {
          const { data: attendanceData } = await supabase
            .from("class_attendance")
            .select("date")
            .eq("student_id", student.id)
            .eq("class_id", selectedClass.id)
            .eq("status", "present")
            .order("date", { ascending: false })
            .limit(10);
            
          if (attendanceData && attendanceData.length > 0) {
            let streak = 0;
            const today = new Date();
            const oneWeek = 7 * 24 * 60 * 60 * 1000;
            
            for (let i = 0; i < attendanceData.length; i++) {
              const attendanceDate = new Date(attendanceData[i].date);
              const expectedDate = new Date(today.getTime() - (i * oneWeek));
              
              if (Math.abs(attendanceDate.getTime() - expectedDate.getTime()) < oneWeek) {
                streak++;
              } else {
                break;
              }
            }
            
            if (streak > 1) {
              streaks[student.id] = streak;
            }
          }
        }
        setStudentStreaks(streaks);
      }
      
      // Get staff info for this class - using proper instructor relationship
      const { data: classData } = await supabase
        .from("classes")
        .select("instructor_id, user_profiles!classes_instructor_id_fkey(id, first_name, last_name, profile_picture_url)")
        .eq("id", selectedClass.id)
        .single();
        
      if (classData && classData.user_profiles) {
        setStaffInfo(classData.user_profiles);
      }
      
      setLoading(false);
    };
    fetchStudents();
  }, [selectedClass]);

  // Fetch attendance for selected class (today)
  useEffect(() => {
    if (!selectedClass) return;
    const today = new Date().toISOString().slice(0, 10);
    const fetchAttendance = async () => {
      const records = await AttendanceService.getAttendanceByClass(selectedClass.id, today);
      const checked: Record<string, AttendanceRecord> = {};
      records.forEach((rec) => {
        if (rec.status === "present") checked[rec.student_id] = rec;
      });
      setCheckedIn(checked);
    };
    fetchAttendance();
  }, [selectedClass]);

  const handleCheckIn = async (student: any) => {
    setCheckingIn(student.id);
    const today = new Date().toISOString().slice(0, 10);
    const attendanceData = {
      class_id: selectedClass.id,
      student_id: student.id,
      organization_id: selectedClass.organization_id,
      date: today,
      status: "present" as const,
    };
    
    const result = await AttendanceService.recordAttendance(attendanceData);
    if (result) {
      setCheckedIn((prev) => ({ ...prev, [student.id]: result }));
      const checkInTime = new Date(result.created_at).toLocaleTimeString([], { 
        hour: "2-digit", 
        minute: "2-digit",
        hour12: true
      });
      
      // Play success sound and show celebration
      playSuccessSound();
      setShowCelebration({ student, time: checkInTime });
    }
    setCheckingIn(null);
  };

  const { greeting, emoji, gradient } = getTimeBasedGreeting();

  // Upgrade lock overlay
  if (!isCheckinEnabled) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-70 z-50 flex flex-col items-center justify-center text-white">
        <FaLock size={64} className="mb-6 text-yellow-400" />
        <h2 className="text-4xl font-bold mb-4">Upgrade Required</h2>
        <p className="text-xl text-center">Check-in Kiosk is a premium feature.<br />Upgrade your plan to unlock instant check-ins!</p>
      </div>
    );
  }

  // Step 1: Class selection
  if (!selectedClass) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-indigo-50 via-white to-blue-50 flex flex-col">
        {/* Header */}
        <motion.header 
          className={`bg-gradient-to-r ${gradient} text-white shadow-lg`}
          initial={{ y: -100 }}
          animate={{ y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <div className="container mx-auto px-8 py-8">
            <motion.div 
              className="text-center"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.6 }}
            >
              <h1 className="text-5xl font-bold mb-4">
                {greeting} {emoji}
              </h1>
              <h2 className="text-3xl font-semibold">Check-In Kiosk</h2>
              <p className="text-xl mt-2 opacity-90">Select your class to get started</p>
            </motion.div>
          </div>
        </motion.header>

        {/* Main content */}
        <main className="flex-1 flex items-center justify-center p-8">
          <motion.div 
            className="max-w-2xl w-full"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
          >
            {/* Express Check-in for returning families */}
            {lastWeekClass && classes.find(c => c.id === lastWeekClass) && (
              <motion.div
                className="bg-gradient-to-r from-purple-100 to-blue-100 rounded-3xl shadow-xl p-6 mb-6"
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.6, duration: 0.5 }}
              >
                <h3 className="text-xl font-bold text-gray-800 mb-3">⚡ Express Check-In</h3>
                <p className="text-gray-600 mb-4">Welcome back! Check into your usual class?</p>
                <motion.button
                  className="w-full bg-gradient-to-r from-purple-500 to-blue-600 text-white px-6 py-4 rounded-2xl font-bold text-xl shadow-lg hover:shadow-xl transition-all"
                  onClick={() => {
                    const cls = classes.find(c => c.id === lastWeekClass);
                    if (cls) setSelectedClass(cls);
                  }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {classes.find(c => c.id === lastWeekClass)?.name} →
                </motion.button>
              </motion.div>
            )}
            
            <div className="bg-white rounded-3xl shadow-2xl p-8">
              <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">
                Choose Your Class
              </h3>
              
              <select
                className="w-full text-2xl p-6 rounded-2xl border-2 border-gray-300 focus:border-blue-500 focus:outline-none transition-colors font-semibold bg-white shadow-inner"
                value=""
                onChange={e => {
                  const cls = classes.find(c => c.id === e.target.value);
                  setSelectedClass(cls || null);
                }}
              >
                <option value="">Select a class...</option>
                {classes.map(cls => (
                  <option key={cls.id} value={cls.id}>
                    {cls.name} {cls.level ? `(${cls.level})` : ""} {cls.location ? `- ${cls.location}` : ""}
                  </option>
                ))}
              </select>

              {classes.length === 0 && (
                <div className="text-center mt-6 text-gray-500">
                  <FaClock className="mx-auto mb-2" size={24} />
                  <p>No classes scheduled for today</p>
                </div>
              )}
            </div>
          </motion.div>
        </main>
      </div>
    );
  }

  // Step 2: Student check-in interface
  const classTime = selectedClass.class_schedules?.[0]?.start_time ?
    formatTimeRange(selectedClass.class_schedules[0].start_time, selectedClass.class_schedules[0].end_time) : "";

  const checkedCount = Object.keys(checkedIn).length;
  const totalCount = students.length;

  return (
    <>
      <div className="fixed inset-0 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex flex-col">
        {/* Header */}
        <motion.header 
          className="bg-white shadow-lg border-b-4 border-blue-200"
          initial={{ y: -100 }}
          animate={{ y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="container mx-auto px-8 py-6">
            <div className="flex items-center justify-between">
              <motion.button
                onClick={() => setSelectedClass(null)}
                className="flex items-center gap-3 px-6 py-3 bg-gray-100 hover:bg-gray-200 rounded-2xl transition-colors font-semibold text-gray-700"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <FaArrowLeft /> Change Class
              </motion.button>
              
              <div className="text-center flex-1">
                <h1 className="text-4xl font-bold text-gray-800 mb-2">
                  {selectedClass.name} {selectedClass.level ? `(${selectedClass.level})` : ""}
                </h1>
                <div className="text-xl text-gray-600 flex items-center justify-center gap-4">
                  <span className="flex items-center gap-2">
                    <FaClock /> {classTime}
                  </span>
                  {selectedClass.location && (
                    <span>📍 {selectedClass.location}</span>
                  )}
                </div>
                {staffInfo && (
                  <motion.div 
                    className="mt-3 flex items-center justify-center gap-3"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                  >
                    <span className="text-lg text-gray-700 font-medium">Today's Instructor:</span>
                    {staffInfo.profile_picture_url ? (
                      <img 
                        src={staffInfo.profile_picture_url} 
                        alt={staffInfo.first_name}
                        className="w-10 h-10 rounded-full border-2 border-gray-300"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center text-gray-600">
                        <FaUsers size={20} />
                      </div>
                    )}
                    <span className="text-lg font-bold text-gray-800">
                      {staffInfo.first_name} {staffInfo.last_name}
                    </span>
                  </motion.div>
                )}
              </div>
              
              <div className="text-right">
                <div className={`
                  px-6 py-3 rounded-2xl font-bold text-xl flex items-center gap-2
                  ${checkedCount >= totalCount * 0.9 
                    ? 'bg-gradient-to-r from-red-400 to-orange-500 text-white' 
                    : 'bg-gradient-to-r from-green-400 to-blue-500 text-white'
                  }
                `}>
                  <FaUsers />
                  {checkedCount} / {totalCount}
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  {totalCount - checkedCount} spots available
                </p>
              </div>
            </div>
          </div>
        </motion.header>

        {/* Main content */}
        <main className="flex-1 overflow-auto p-8">
          {students.length === 0 ? (
            <motion.div 
              className="flex flex-col items-center justify-center h-full text-gray-500"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              <FaUsers size={64} className="mb-4" />
              <h3 className="text-3xl font-semibold mb-2">No Students Enrolled</h3>
              <p className="text-xl">This class doesn't have any active enrollments.</p>
            </motion.div>
          ) : (
            <motion.div 
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 max-w-7xl mx-auto"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              {students.map((student, index) => (
                <motion.div
                  key={student.id}
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                >
                  <StudentCard
                    student={student}
                    checked={checkedIn[student.id]}
                    isLoading={checkingIn === student.id}
                    onCheckIn={handleCheckIn}
                    streak={studentStreaks[student.id] || 0}
                  />
                </motion.div>
              ))}
            </motion.div>
          )}
        </main>

        {/* Future features hint */}
        <motion.footer 
          className="text-center py-6 text-gray-400"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 1 }}
        >
          <p className="text-lg">🔮 QR code check-in coming soon!</p>
        </motion.footer>
      </div>

      {/* Success Animation Overlay */}
      <AnimatePresence>
        {showCelebration && (
          <SuccessAnimation
            student={showCelebration.student}
            time={showCelebration.time}
            onComplete={() => setShowCelebration(null)}
          />
        )}
      </AnimatePresence>

      {/* Custom animations */}
      <style>{`
        .kiosk-fullscreen {
          overflow: hidden;
        }
        
        @keyframes pulse-glow {
          0%, 100% { box-shadow: 0 0 20px rgba(59, 130, 246, 0.5); }
          50% { box-shadow: 0 0 40px rgba(59, 130, 246, 0.8); }
        }
        
        .pulse-glow {
          animation: pulse-glow 2s infinite;
        }
      `}</style>
    </>
  );
};

export default CheckIn;